import axios from 'axios';
import { getNoCache } from 'helpers/utils/NoCacheUtils';
import { EnvironmentNames } from 'models/environment.model';

let loadedEnvironment: IApis;

export interface IEnpointApis {
  assembleiasAssessor: string;
  assembleiasAssessorBff: string;
  assembleiasRelatoriosAssessor: string;
  assembleiasAssessorMessenger: string;
  assembleiasAssociado: string;
  assembleiasNotificacao: string;
  assembleiasVoto: string;
  fundacaoBff: string;
  duvidasFrequentes: string;
  eventoAssociado: string;
  relatoriosAssessor: string;
  coopsExterno: string;
}

export interface IWebsockets {
  use: 'wsAssembleia' | 'sseAssembleia';
  wsAssembleia: string;
  sseAssembleia: string;
}

export interface IEnpointWSO2Auth {
  WSO2: string;
  authUrl: string;
  tokenUrl: string;
  wso2LogoutUrl: string;
  identityClientId: string;
  redirectUrl: string;
  logofRedirectUrl: string;
}

export interface IAuth {
  authority: string;
  redirect_uri: string;
  post_logout_redirect_uri: string;
  client_id: string;
  scope: string;
  response_type: string;
  metadata: {
    issuer: string;
    authorization_endpoint: string;
    token_endpoint: string;
    introspection_endpoint: string;
    userinfo_endpoint: string;
    end_session_endpoint: string;
    jwks_uri: string;
    check_session_ifram: string;
  };
}

export interface IApis {
  environment: EnvironmentNames;
  isOffline: boolean;
  gtag: boolean;
  captchaKey: string;
  handtalk: boolean;
  apis: IEnpointApis;
  links: {
    pertencer: string;
    pesquisa: string;
    pesquisareacao: string;
  };
  cookies: {
    active: boolean;
    uuid: string;
    env: string;
  };
  websockets: IWebsockets;
  authAssessor: IAuth;
  authAssociado: IAuth;
}

export const getAssembleiasAssessorEndpoint = (version: string = '1') => {
  return loadedEnvironment?.apis?.assembleiasAssessor
    ? `${loadedEnvironment.apis.assembleiasAssessor}v${version}/`
    : '';
};

export const getAssembleiasAssessorBffEndpoint = (version: string = '1') => {
  return loadedEnvironment?.apis?.assembleiasAssessorBff
    ? `${loadedEnvironment.apis.assembleiasAssessorBff}v${version}/`
    : '';
};

export const getAssembleiasAssessorRelatoriosEndpoint = (
  version: string = '1'
) => {
  return loadedEnvironment?.apis?.assembleiasRelatoriosAssessor
    ? `${loadedEnvironment.apis.assembleiasRelatoriosAssessor}v${version}/`
    : '';
};

export const getAssembleiasAssessorMessengerEndpoint = (
  version: string = '1'
) => {
  return loadedEnvironment?.apis?.assembleiasAssessorMessenger
    ? `${loadedEnvironment.apis.assembleiasAssessorMessenger}v${version}/`
    : '';
};

export const getAssembleiasAssociadoEndpoint = (version: string = '1') => {
  return loadedEnvironment?.apis?.assembleiasAssociado
    ? `${loadedEnvironment.apis.assembleiasAssociado}v${version}/`
    : '';
};

export const getAssembleiasNotificacaoEndpoint = (version: string = '1') => {
  return loadedEnvironment?.apis?.assembleiasNotificacao
    ? `${loadedEnvironment.apis.assembleiasNotificacao}v${version}/`
    : '';
};

export const getAssembleiasVotoEndpoint = (version: string = '1') => {
  return loadedEnvironment?.apis?.assembleiasVoto
    ? `${loadedEnvironment.apis.assembleiasVoto}v${version}/`
    : '';
};

export const getFundacaoBffEndpoint = (version: string = '1') => {
  return loadedEnvironment?.apis?.fundacaoBff
    ? `${loadedEnvironment.apis.fundacaoBff}v${version}/`
    : '';
};

export const getDuvidasFrequentesEndpoint = (version: string = '1') => {
  return loadedEnvironment?.apis?.duvidasFrequentes
    ? `${loadedEnvironment.apis.duvidasFrequentes}v${version}/`
    : '';
};

export const getEventoAssociadoEndpoint = (version: string = '1') => {
  return loadedEnvironment?.apis?.eventoAssociado
    ? `${loadedEnvironment.apis.eventoAssociado}v${version}/`
    : '';
};

export const getCoopsExternoEndpoint = (version: string = '1') => {
  return loadedEnvironment?.apis?.coopsExterno
    ? `${loadedEnvironment.apis.coopsExterno}v${version}/`
    : '';
};

export const getSocketTechToUse = (): 'wsAssembleia' | 'sseAssembleia' | '' => {
  return loadedEnvironment?.websockets?.use || '';
};

export const getWSAssembleiasEndpoint = (): string => {
  return loadedEnvironment?.websockets?.wsAssembleia || '';
};

export const getSSEAssembleiasEndpoint = (): string => {
  return loadedEnvironment?.websockets?.sseAssembleia || '';
};

export const getApiEndpoint = (api: keyof IEnpointApis, version?: string) => {
  switch (api) {
    case 'assembleiasAssessor':
      return getAssembleiasAssessorEndpoint(version);
    case 'assembleiasAssessorMessenger':
      return getAssembleiasAssessorMessengerEndpoint(version);
    case 'assembleiasAssociado':
      return getAssembleiasAssociadoEndpoint(version);
    case 'assembleiasVoto':
      return getAssembleiasVotoEndpoint(version);
    case 'fundacaoBff':
      return getFundacaoBffEndpoint(version);
    case 'duvidasFrequentes':
      return getDuvidasFrequentesEndpoint(version);
    case 'eventoAssociado':
      return getEventoAssociadoEndpoint(version);
    case 'assembleiasAssessorBff':
      return getAssembleiasAssessorBffEndpoint(version);
    case 'coopsExterno':
      return getCoopsExternoEndpoint(version);
    default:
      return;
  }
};

export const loadEnvironments = async () => {
  try {
    const response = await axios.get<IApis>(
      `assets/environments/default.json?noCache=${getNoCache()}`
    );
    loadedEnvironment = response.data;
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to load environments');
  }
};

export const getApis = () => {
  return loadedEnvironment;
};
